import Urn from '@adc/structures/urn'
import React from 'react'
import {
  TextCell,
  DateCell,
  COLUMN_SORT_SETTING,
  COLUMN_TYPES,
  // @ts-ignore cannot find declaration file
} from '@adc/datatable'
// @ts-ignore no types
import { Icon } from '@adc/ui-morpheus'
import { filters } from '@adc/blueprint'

const NOT_SORTABLE = -1

const currentStandardColProps = {
  isResizable: true,
  isReorderable: true,
  isVisible: true,
  searchable: false,
  align: 'left',
  flexGrow: 0,
  sort: COLUMN_SORT_SETTING.NOT_SORTED,
}

interface ActionHandlers {
  onRowAction: (rowIndex: number) => void
}

interface GenerateColumnsParams {
  actionHandlers: ActionHandlers
}

export const generateColumns = ({ actionHandlers }: GenerateColumnsParams) => {
  return [
    {
      id: 'name',
      field: 'name',
      title: 'Organization',
      Cell: TextCell,
      apiRef: 'name',
      sortField: 'name.keyword',
      accessor: (row: Record<string, unknown>) => row.name,
      colProps: {
        ...currentStandardColProps,
        width: 280,
        searchable: true,
        sort: COLUMN_SORT_SETTING.SORTED,
        sortAscending: true,
      },
    },
    {
      id: 'is_trusted',
      field: 'is_trusted',
      title: 'Trusted',
      Cell: ({ value }: { value: Record<string, unknown> }) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {Boolean(value.is_trusted) && (
            <Icon
              name='checkmark'
              color='success'
              title='Trusted organization'
              elmId={`org_trusted_badge_${value.id}`}
            />
          )}
        </div>
      ),
      apiRef: 'is_trusted',
      accessor: (row: Record<string, unknown>) => row,
      colProps: {
        ...currentStandardColProps,
        width: 90,
        align: 'center',
        sort: NOT_SORTABLE,
      },
    },
    {
      id: 'types',
      field: 'types',
      title: 'Types',
      type: COLUMN_TYPES.LABEL,
      apiRef: 'types',
      colProps: {
        ...currentStandardColProps,
        width: 250,
        searchable: true,
        sort: NOT_SORTABLE,
      },
    },
    {
      id: 'aliases',
      field: 'aliases',
      title: 'Aliases',
      type: COLUMN_TYPES.LABEL,
      apiRef: 'aliases',
      colProps: {
        ...currentStandardColProps,
        width: 250,
        sort: NOT_SORTABLE,
      },
    },
    {
      id: 'allowed_domains',
      field: 'allowed_domains',
      title: 'Allowed Domains',
      type: COLUMN_TYPES.LABEL,
      apiRef: 'allowed_domains',
      colProps: {
        ...currentStandardColProps,
        width: 250,
        sort: NOT_SORTABLE,
      },
    },
    {
      id: 'created_date',
      field: 'created_date',
      title: 'Created Date',
      Cell: (props: Record<string, unknown>) => (
        <DateCell {...props} format='MMM D, YYYY h:mm a' />
      ),
      apiRef: 'created_date',
      sortField: 'created_date',
      accessor: (row: Record<string, unknown>) => row.created_date,
      colProps: {
        ...currentStandardColProps,
        width: 160,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'updated_date',
      field: 'updated_date',
      title: 'Updated Date',
      Cell: (props: Record<string, unknown>) => (
        <DateCell {...props} format='MMM D, YYYY h:mm a' />
      ),
      apiRef: 'updated_date',
      sortField: 'updated_date',
      accessor: (row: Record<string, unknown>) => row.updated_date,
      colProps: {
        ...currentStandardColProps,
        width: 160,
        sort: COLUMN_SORT_SETTING.NOT_SORTED,
      },
    },
    {
      id: 'actions',
      field: '__actions__',
      title: 'Actions',
      Cell: ({
        rowIndex,
        value,
      }: {
        rowIndex: number
        value: Record<string, unknown>
      }) => {
        const row = value
        const orgId = row.id as string
        const viewState = filters.utils.serializePageParams({
          filters: {
            organization: [new Urn('organization', orgId).toString()],
          },
        })
        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <a
              href={`/organizations/activitytimeline?view_state=${viewState}`}
              style={{ display: 'inline-flex' }}
            >
              <Icon
                color='info'
                size='large'
                style={{ cursor: 'pointer' }}
                name='eye'
                title='View Activity Timeline'
                data-elm-id={`action_activity_timeline_${rowIndex}`}
              />
            </a>
            <a
              href={`/organizations/${orgId}/edit`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                actionHandlers.onRowAction(rowIndex)
              }}
              style={{ display: 'inline-flex' }}
            >
              <Icon
                color='info'
                size='large'
                style={{ cursor: 'pointer' }}
                name='write square'
                title='View / Edit Organization'
                data-elm-id={`action_edit_${rowIndex}`}
              />
            </a>
          </div>
        )
      },
      apiRef: '__actions__',
      accessor: (row: Record<string, unknown>) => row,
      colProps: {
        width: 100,
        fixedRight: true,
        align: 'center',
        isResizable: false,
        isReorderable: false,
        isVisible: true,
        searchable: false,
        flexGrow: 0,
        sort: NOT_SORTABLE,
      },
    },
  ]
}
